<template>
  <div class="lineDiffWrap">
    <div class="lineDiffHeader">
      <div>{{ leftLabel }}</div>
      <div>{{ rightLabel }}</div>
    </div>
    <div
      v-for="(row, idx) in rows"
      :key="idx"
      class="lineDiffRow"
      :class="`lineDiff-${row.type}`"
    >
      <div class="lineCell">
        <span class="lineNo">{{ row.leftNo || '' }}</span>
        <span class="lineText">
          <template v-if="row.leftSpans">
            <span v-for="(sp, si) in row.leftSpans" :key="si" :class="{ wordRemoved: sp.type === 'removed' }">{{ sp.text }}</span>
          </template>
          <template v-else>{{ row.leftText }}</template>
        </span>
      </div>
      <div class="lineCell">
        <span class="lineNo">{{ row.rightNo || '' }}</span>
        <span class="lineText">
          <template v-if="row.rightSpans">
            <span v-for="(sp, si) in row.rightSpans" :key="si" :class="{ wordAdded: sp.type === 'added' }">{{ sp.text }}</span>
          </template>
          <template v-else>{{ row.rightText }}</template>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  before: { type: String, default: '' },
  after: { type: String, default: '' },
  leftLabel: { type: String, default: 'Before' },
  rightLabel: { type: String, default: 'After' },
})

function buildWordSpans(oldText, newText) {
  const oldTokens = (oldText || '').split(/(\s+)/).filter((t) => t.length > 0)
  const newTokens = (newText || '').split(/(\s+)/).filter((t) => t.length > 0)
  const m = oldTokens.length
  const n = newTokens.length
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (oldTokens[i] === newTokens[j]) dp[i][j] = dp[i + 1][j + 1] + 1
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }
  const leftSpans = []
  const rightSpans = []
  let i = 0
  let j = 0
  while (i < m && j < n) {
    if (oldTokens[i] === newTokens[j]) {
      leftSpans.push({ type: 'equal', text: oldTokens[i] })
      rightSpans.push({ type: 'equal', text: newTokens[j] })
      i += 1
      j += 1
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      leftSpans.push({ type: 'removed', text: oldTokens[i] })
      i += 1
    } else {
      rightSpans.push({ type: 'added', text: newTokens[j] })
      j += 1
    }
  }
  while (i < m) { leftSpans.push({ type: 'removed', text: oldTokens[i] }); i += 1 }
  while (j < n) { rightSpans.push({ type: 'added', text: newTokens[j] }); j += 1 }
  return { leftSpans, rightSpans }
}

function buildRows(beforeText, afterText) {
  const oldLines = String(beforeText || '').split('\n')
  const newLines = String(afterText || '').split('\n')
  const m = oldLines.length
  const n = newLines.length
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (oldLines[i] === newLines[j]) dp[i][j] = dp[i + 1][j + 1] + 1
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }

  const rows = []
  let i = 0
  let j = 0
  let oldNo = 1
  let newNo = 1
  while (i < m && j < n) {
    if (oldLines[i] === newLines[j]) {
      rows.push({ type: 'equal', leftNo: oldNo, leftText: oldLines[i], rightNo: newNo, rightText: newLines[j] })
      i += 1; j += 1; oldNo += 1; newNo += 1
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      rows.push({ type: 'removed', leftNo: oldNo, leftText: oldLines[i], rightNo: '', rightText: '' })
      i += 1; oldNo += 1
    } else {
      rows.push({ type: 'added', leftNo: '', leftText: '', rightNo: newNo, rightText: newLines[j] })
      j += 1; newNo += 1
    }
  }
  while (i < m) { rows.push({ type: 'removed', leftNo: oldNo, leftText: oldLines[i], rightNo: '', rightText: '' }); i += 1; oldNo += 1 }
  while (j < n) { rows.push({ type: 'added', leftNo: '', leftText: '', rightNo: newNo, rightText: newLines[j] }); j += 1; newNo += 1 }

  // Pair adjacent removed+added into modified rows with word-level spans
  const paired = []
  let k = 0
  while (k < rows.length) {
    const row = rows[k]
    if (row.type === 'removed' && k + 1 < rows.length && rows[k + 1].type === 'added') {
      const next = rows[k + 1]
      const { leftSpans, rightSpans } = buildWordSpans(row.leftText, next.rightText)
      paired.push({ type: 'modified', leftNo: row.leftNo, leftText: row.leftText, leftSpans, rightNo: next.rightNo, rightText: next.rightText, rightSpans })
      k += 2
    } else {
      paired.push(row)
      k += 1
    }
  }
  return paired
}

const rows = computed(() => buildRows(props.before, props.after))
</script>

<style scoped>
.lineDiffWrap {
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

.lineDiffHeader,
.lineDiffRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.lineDiffHeader {
  background: var(--table-header-bg);
  font-size: 11px;
  font-weight: 700;
  color: var(--table-header-text);
}

.lineDiffHeader > div {
  padding: 6px 8px;
  border-bottom: 1px solid var(--border);
}

.lineDiffHeader > div:first-child,
.lineDiffRow > .lineCell:first-child {
  border-right: 1px solid var(--border);
}

.lineCell {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 8px;
  padding: 4px 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.35;
}

.lineNo {
  color: var(--muted);
  text-align: right;
}

.lineText {
  white-space: pre-wrap;
  word-break: break-word;
}

.lineDiff-removed .lineCell:first-child,
.lineDiff-modified .lineCell:first-child {
  background: var(--diff-removed-bg);
}

.lineDiff-added .lineCell:last-child,
.lineDiff-modified .lineCell:last-child {
  background: var(--diff-added-bg);
}

.wordRemoved {
  background: var(--diff-removed-word);
  border-radius: 2px;
}

.wordAdded {
  background: var(--diff-added-word);
  border-radius: 2px;
}
</style>
